import { useState, useEffect } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(/\/api$/, '');
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("expertnet_token")}`,
});

const TIER_ORDER = ["high_priority", "active", "long_term", "on_hold"];
const TIER_LABELS = {
  high_priority: "High Priority",
  active: "Active",
  long_term: "Long Term",
  on_hold: "On Hold",
};
const TIER_KEYS = ["high_priority", "active", "long_term", "on_hold"];

const inputStyle = {
  width: "100%", padding: "9px 12px", background: "var(--rasp-raised)",
  border: "1px solid var(--rasp-border)", borderRadius: 8,
  color: "var(--rasp-fg-1)", fontFamily: "'Inter', sans-serif", fontSize: 13,
  outline: "none", boxSizing: "border-box",
};
const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 700, color: "var(--rasp-fg-2)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6,
};
const panelStyle = {
  background: "var(--rasp-surface)", border: "1px solid var(--rasp-border)",
  borderRadius: 10, padding: 20,
  display: "flex", flexDirection: "column", gap: 14, marginBottom: 20,
};

function TierToggle({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {TIER_KEYS.map(t => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          style={{
            padding: "5px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600,
            cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.04em",
            background: value === t ? "var(--rasp-teal-soft)" : "transparent",
            border: `1px solid ${value === t ? "var(--rasp-teal-edge)" : "var(--rasp-border)"}`,
            color: value === t ? "var(--rasp-teal-light)" : "var(--rasp-fg-3)",
          }}
        >{TIER_LABELS[t]}</button>
      ))}
    </div>
  );
}

function StatusChip({ status }) {
  const isAccepted = status === "accepted";
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: isAccepted ? 'var(--rasp-teal-light)' : 'var(--rasp-teal-light)',
      background: isAccepted ? 'var(--rasp-teal-soft)' : 'var(--rasp-teal-soft)',
      border: `1px solid ${isAccepted ? 'var(--rasp-teal-edge)' : 'var(--rasp-teal-edge)'}`,
      borderRadius: 20, padding: '3px 10px', textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>{status}</span>
  );
}

export default function ManagerDashboard({ currentUser, onLogout }) {
  const [mainTab, setMainTab] = useState("runs");
  const [overviewData, setOverviewData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ associate_id: "", ticker: "", context: "", priority_tier: "active" });
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editForm, setEditForm] = useState({ ticker: "", context: "", priority_tier: "active" });
  const [activeAssociate, setActiveAssociate] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [notes, setNotes] = useState({});
  const [newNote, setNewNote] = useState({});
  const [archivedAssignments, setArchivedAssignments] = useState([]);
  const [selectedArchived, setSelectedArchived] = useState(new Set());
  const [expandedAssociates, setExpandedAssociates] = useState({});

  useEffect(() => {
    fetchAssignments();
    fetchAssociates();
  }, []);

  useEffect(() => {
    if (mainTab !== "overview") return;
    fetch(`${API}/api/manager/overview`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setOverviewData(d); })
      .catch(() => {});
  }, [mainTab]);

  useEffect(() => {
    if (mainTab !== "archives") return;
    fetchArchivedAssignments();
  }, [mainTab]);

  const fetchAssignments = async () => {
    try {
      const res = await fetch(`${API}/api/assignments`, { headers: authHeaders() });
      if (res.ok) setAssignments(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchAssociates = async () => {
    try {
      const res = await fetch(`${API}/api/users`, { headers: authHeaders() });
      if (res.ok) {
        const all = await res.json();
        setAssociates(all.filter(u => u.role === "associate"));
      }
    } catch (e) { console.error(e); }
  };

  const fetchNotes = async (assignmentId) => {
    try {
      const res = await fetch(`${API}/api/assignments/${assignmentId}/notes`, { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setNotes(prev => ({ ...prev, [assignmentId]: data })); }
    } catch (e) { console.error(e); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/assignments`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          associate_id: parseInt(newAssignment.associate_id),
          ticker: newAssignment.ticker,
          context: newAssignment.context,
          priority_tier: newAssignment.priority_tier,
        }),
      });
      if (res.ok) {
        setNewAssignment({ associate_id: "", ticker: "", context: "", priority_tier: "active" });
        setShowAssignForm(false);
        fetchAssignments();
      }
    } catch (e) { console.error(e); }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const id = editingAssignment.id;
    try {
      await fetch(`${API}/api/assignments/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ ticker: editForm.ticker, context: editForm.context }),
      });
      await fetch(`${API}/api/assignments/${id}/priority`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ priority_tier: editForm.priority_tier, priority_order: 0 }),
      });
      setEditingAssignment(null);
      fetchAssignments();
    } catch (e) { console.error(e); }
  };

  const toggleNotes = (assignmentId) => {
    const nowOpen = !expandedNotes[assignmentId];
    setExpandedNotes(prev => ({ ...prev, [assignmentId]: nowOpen }));
    if (nowOpen && !notes[assignmentId]) fetchNotes(assignmentId);
  };

  const handleAddNote = async (assignmentId) => {
    const text = (newNote[assignmentId] || "").trim();
    if (!text) return;
    try {
      const res = await fetch(`${API}/api/assignments/${assignmentId}/notes`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ note_text: text }),
      });
      if (res.ok) {
        setNewNote(prev => ({ ...prev, [assignmentId]: "" }));
        fetchNotes(assignmentId);
      }
    } catch (e) { console.error(e); }
  };

  const archiveAssignment = async (projectId) => {
    if (!window.confirm('Are you sure you want to archive this project? It will be removed from the active project run.')) return;
    await fetch(`${API}/api/projects/${projectId}/archive`, { method: 'PATCH', headers: authHeaders() });
    fetchAssignments();
  };

  const unarchiveProject = async (projectId) => {
    await fetch(`${API}/api/projects/${projectId}/unarchive`, { method: 'PATCH', headers: authHeaders() });
    fetchArchivedAssignments();
    fetchAssignments();
  };

  const fetchArchivedAssignments = async () => {
    try {
      const res = await fetch(`${API}/api/projects/archived`, { headers: authHeaders() });
      if (res.ok) setArchivedAssignments(await res.json());
    } catch (e) { console.error(e); }
  };

  const openEdit = (a) => {
    setEditingAssignment(a);
    setEditForm({ ticker: a.ticker || "", context: a.context || "", priority_tier: a.priority_tier || "active" });
  };

  const toggleAssociate = (id) => setExpandedAssociates(prev => ({ ...prev, [id]: !prev[id] }));

  const tierColor = { high_priority: '#e07050', active: 'var(--rasp-teal-light)', long_term: '#5ab4cc', on_hold: '#4d7a90' };

  const associateGroupMap = {};
  for (const a of assignments) {
    if (!associateGroupMap[a.associate_id]) {
      associateGroupMap[a.associate_id] = { associate_id: a.associate_id, associate_username: a.associate_username, assignments: [] };
    }
    associateGroupMap[a.associate_id].assignments.push(a);
  }
  const associateGroups = Object.values(associateGroupMap).sort((a, b) => (a.associate_username || '').localeCompare(b.associate_username || ''));

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)", fontFamily: "'Inter', sans-serif", width: '100vw' }}>

      {/* Sidebar */}
      <div style={{
        width: 220, minWidth: 220, background: "rgba(5,12,20,0.95)",
        borderRight: "1px solid var(--rasp-border)", display: "flex",
        flexDirection: "column", padding: "14px 10px", minHeight: "100vh",
      }}>
        <div style={{ padding: "6px 10px 4px" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--rasp-teal-light)", letterSpacing: "-0.01em" }}>
            Rasp
          </div>
          <div style={{ fontSize: 12, color: "var(--rasp-fg-3)", marginTop: 2 }}>Manager</div>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ padding: "10px 10px 6px", borderTop: "1px solid var(--rasp-border)" }}>
          <div style={{ fontSize: 12, color: "var(--rasp-fg-2)", marginBottom: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {currentUser.username}
          </div>
          <button
            onClick={onLogout}
            style={{
              width: "100%", padding: "7px 0", background: "transparent",
              border: "1px solid var(--rasp-border)", borderRadius: 7,
              color: "var(--rasp-fg-3)", fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}
          >Log Out</button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflowY: "auto" }}>

        {/* Top-level tab bar */}
        <div style={{ borderBottom: "1px solid var(--rasp-border)", padding: "0 28px", display: "flex", gap: 0, flexShrink: 0, width: '100%', boxSizing: 'border-box' }}>
          {[{ id: "runs", label: "Project Runs" }, { id: "overview", label: "Overview" }, { id: "archives", label: "Archives" }].map(t => (
            <button
              key={t.id}
              onClick={() => setMainTab(t.id)}
              style={{
                padding: "14px 18px", fontSize: 13, fontWeight: 600,
                background: "transparent", border: "none", cursor: "pointer",
                color: mainTab === t.id ? "var(--rasp-teal-light)" : "var(--rasp-fg-3)",
                borderBottom: mainTab === t.id ? "2px solid var(--rasp-teal-light)" : "2px solid transparent",
                marginBottom: -1,
              }}
            >{t.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, padding: "24px 28px", overflowY: "auto", width: '100%', boxSizing: 'border-box' }}>
        <div style={{ margin: "0 auto", width: "100%" }}>

        {/* Overview panel */}
        {mainTab === "overview" && (
          !overviewData ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
              <span style={{ color: "var(--rasp-fg-3)", fontSize: 14 }}>Loading...</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

              {/* 4 stat cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {[
                  { label: "Total Projects",  value: overviewData.total_projects },
                  { label: "Experts Sourced", value: overviewData.total_experts },
                  { label: "Pitched",         value: overviewData.total_pitched },
                  { label: "Engaged",         value: overviewData.total_engaged },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: "var(--rasp-surface)", border: "1px solid var(--rasp-border)", borderRadius: 8, padding: 20 }}>
                    <div style={{ fontSize: 12, color: "var(--rasp-fg-3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{label}</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: "var(--rasp-teal-light)" }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Projects by Tier */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--rasp-teal-light)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>Projects by Tier</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(() => {
                    const max = Math.max(1, ...overviewData.projects_by_tier.map(r => r.count));
                    return overviewData.projects_by_tier.map(r => (
                      <div key={r.tier} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 100, fontSize: 13, color: "var(--rasp-fg-2)", flexShrink: 0 }}>{r.tier ?? "—"}</div>
                        <div style={{ flex: 1, height: 10, background: "var(--rasp-raised)", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ width: `${(r.count / max) * 100}%`, height: "100%", background: "var(--rasp-teal-light)", opacity: 0.3 }} />
                        </div>
                        <div style={{ width: 32, fontSize: 13, color: "var(--rasp-fg-2)", textAlign: "right" }}>{r.count}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Top Associates */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--rasp-teal-light)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>Top Associates</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {overviewData.top_associates.map(r => (
                    <div key={r.username} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: "var(--rasp-fg-2)" }}>{r.username}</span>
                      <span style={{ fontSize: 13, color: "var(--rasp-teal-light)", fontWeight: 600 }}>{r.project_count}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )
        )}

        {mainTab === "runs" && (<>

        {/* Top bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontSize: 20, fontWeight: 600, color: "var(--rasp-teal-light)" }}>Project Runs</span>
          <button
            onClick={() => { setShowAssignForm(v => !v); setEditingAssignment(null); }}
            style={{
              background: 'var(--rasp-grad)', border: 'none',
              color: '#ffffff', borderRadius: 8, fontSize: 13, fontWeight: 600, padding: '8px 18px', cursor: 'pointer',
              boxShadow: 'var(--rasp-elev)',
            }}
          >{showAssignForm ? "Cancel" : "Assign Project"}</button>
        </div>

        {/* Assign form */}
        {showAssignForm && (
          <form onSubmit={handleAssign} style={panelStyle}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--rasp-fg-1)" }}>New Assignment</div>
            <div>
              <label style={labelStyle}>Associate</label>
              <select
                value={newAssignment.associate_id}
                onChange={e => setNewAssignment(a => ({ ...a, associate_id: e.target.value }))}
                required
                style={{ ...inputStyle, appearance: "none" }}
              >
                <option value="">Select associate…</option>
                {associates.map(u => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Ticker</label>
              <input
                style={inputStyle}
                value={newAssignment.ticker}
                onChange={e => setNewAssignment(a => ({ ...a, ticker: e.target.value }))}
                required
                onFocus={e => { e.currentTarget.style.borderColor = "var(--rasp-teal-light)"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "var(--rasp-border)"; }}
              />
            </div>
            <div>
              <label style={labelStyle}>Context</label>
              <textarea
                rows={4}
                style={{ ...inputStyle, resize: "vertical" }}
                value={newAssignment.context}
                onChange={e => setNewAssignment(a => ({ ...a, context: e.target.value }))}
                onFocus={e => { e.currentTarget.style.borderColor = "var(--rasp-teal-light)"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "var(--rasp-border)"; }}
              />
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <TierToggle value={newAssignment.priority_tier} onChange={v => setNewAssignment(a => ({ ...a, priority_tier: v }))} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setShowAssignForm(false)}
                style={{ padding: "7px 14px", background: "transparent", border: "1px solid var(--rasp-border)", borderRadius: 7, color: "var(--rasp-fg-3)", fontSize: 12, cursor: "pointer" }}>
                Cancel
              </button>
              <button type="submit"
                style={{ padding: "7px 18px", background: "var(--rasp-grad)", color: "#ffffff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", boxShadow: "var(--rasp-elev)" }}>
                Assign
              </button>
            </div>
          </form>
        )}

        {/* Associate-grouped collapsible list */}
        {associateGroups.length === 0 && (
          <div style={{ color: "var(--rasp-fg-3)", fontSize: 13, textAlign: "center", marginTop: 60 }}>
            No assignments yet.
          </div>
        )}

        {associateGroups.map((group, groupIdx) => {
          const isOpen = !!expandedAssociates[group.associate_id];
          const sortedItems = [...group.assignments].sort((a, b) =>
            TIER_ORDER.indexOf(a.priority_tier) - TIER_ORDER.indexOf(b.priority_tier)
          );
          const tierGroups = TIER_ORDER.map(tier => sortedItems.filter(a => a.priority_tier === tier)).filter(g => g.length > 0);
          return (
            <div key={group.associate_id} style={{ marginBottom: 10, width: '100%' }}>
              {/* Header row */}
              <div
                onClick={() => toggleAssociate(group.associate_id)}
                style={{
                  display: 'flex', alignItems: 'center', padding: '14px 20px',
                  background: 'rgba(8,18,30,0.6)', border: '1px solid var(--rasp-border-soft)',
                  borderRadius: isOpen ? '10px 10px 0 0' : 10,
                  cursor: 'pointer', marginBottom: isOpen ? 0 : 10, width: '100%',
                }}
              >
                <span style={{ color: 'var(--rasp-fg-3)', fontSize: 12, fontWeight: 600, marginRight: 8, minWidth: 16 }}>{groupIdx + 1}.</span>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#22c55e', marginRight: 10, flexShrink: 0 }} />
                <span style={{ color: 'var(--rasp-fg-2)', fontSize: 14, fontWeight: 600, flex: 1 }}>{group.associate_username}</span>
                <span style={{ fontSize: 13, color: 'var(--rasp-fg-2)', fontWeight: 600, marginRight: 16 }}>{group.assignments.length}</span>
                <span style={{ color: 'var(--rasp-fg-3)', fontSize: 11 }}>{isOpen ? '▼' : '▶'}</span>
              </div>

              {/* Expanded content */}
              {isOpen && (
                <div style={{
                  border: '1px solid var(--rasp-border)', borderTop: 'none',
                  borderRadius: '0 0 10px 10px', marginBottom: 12, padding: '8px 16px 16px 16px', width: '100%',
                }}>
                  {(() => { let projectNum = 0; return tierGroups.map((items, idx) => (
                    <div key={items[0].priority_tier}>
                      {idx > 0 && <div style={{ height: 1, background: 'var(--rasp-border-soft)', margin: '8px 0' }} />}
                      {items.map(a => { projectNum++; return (
                        <div key={a.id} style={{
                          borderLeft: `3px solid ${tierColor[a.priority_tier] || 'var(--rasp-border)'}`,
                          padding: '12px 14px', marginBottom: 6,
                          background: 'rgba(255,255,255,0.02)', borderRadius: '0 6px 6px 0',
                        }}>
                          {/* Top row */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--rasp-teal-light)', flex: 1 }}>
                              <span style={{ color: 'var(--rasp-fg-3)', fontSize: 12, fontWeight: 600, marginRight: 6, minWidth: 16 }}>{projectNum}.</span>{a.ticker || '—'}
                            </span>
                            <StatusChip status={a.status} />
                            <button
                              onClick={() => { openEdit(a); setShowAssignForm(false); }}
                              style={{ background: 'transparent', border: '1px solid var(--rasp-border)', borderRadius: 6, color: 'var(--rasp-fg-3)', fontSize: 11, fontWeight: 500, padding: '4px 12px', cursor: 'pointer' }}
                            >Edit</button>
                            <button
                              onClick={() => toggleNotes(a.id)}
                              style={{
                                background: expandedNotes[a.id] ? 'var(--rasp-teal-soft)' : 'transparent',
                                borderRadius: 6, fontSize: 11, fontWeight: 500, padding: '4px 12px', cursor: 'pointer',
                                border: expandedNotes[a.id] ? '1px solid var(--rasp-teal-edge)' : '1px solid var(--rasp-border)',
                                color: expandedNotes[a.id] ? 'var(--rasp-teal-light)' : 'var(--rasp-fg-3)',
                              }}
                            >Notes</button>
                            <button
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); archiveAssignment(a.project_id); }}
                              style={{ background: 'transparent', border: '1px solid var(--rasp-border)', borderRadius: 6, color: 'var(--rasp-fg-3)', fontSize: 11, fontWeight: 500, padding: '4px 12px', cursor: 'pointer' }}
                            >Archive</button>
                          </div>

                          {/* Context */}
                          {a.context && (
                            <div style={{
                              fontSize: 12, color: 'var(--rasp-fg-3)', marginTop: 6,
                              display: '-webkit-box', WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}>{a.context}</div>
                          )}

                          {/* Inline edit form */}
                          {editingAssignment?.id === a.id && (
                            <form onSubmit={handleSaveEdit} style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                              <div>
                                <label style={labelStyle}>Ticker</label>
                                <input
                                  style={inputStyle}
                                  value={editForm.ticker}
                                  onChange={e => setEditForm(f => ({ ...f, ticker: e.target.value }))}
                                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--rasp-teal-light)'; }}
                                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--rasp-border)'; }}
                                />
                              </div>
                              <div>
                                <label style={labelStyle}>Context</label>
                                <textarea
                                  rows={3}
                                  style={{ ...inputStyle, resize: 'vertical' }}
                                  value={editForm.context}
                                  onChange={e => setEditForm(f => ({ ...f, context: e.target.value }))}
                                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--rasp-teal-light)'; }}
                                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--rasp-border)'; }}
                                />
                              </div>
                              <div>
                                <label style={labelStyle}>Priority</label>
                                <TierToggle value={editForm.priority_tier} onChange={v => setEditForm(f => ({ ...f, priority_tier: v }))} />
                              </div>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => setEditingAssignment(null)}
                                  style={{ padding: '6px 12px', background: 'transparent', border: '1px solid var(--rasp-border)', borderRadius: 6, color: 'var(--rasp-fg-3)', fontSize: 11, cursor: 'pointer' }}>
                                  Cancel
                                </button>
                                <button type="submit"
                                  style={{ padding: '6px 14px', background: 'var(--rasp-grad)', color: '#ffffff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', boxShadow: 'var(--rasp-elev)' }}>
                                  Save
                                </button>
                              </div>
                            </form>
                          )}

                          {/* Notes panel */}
                          {expandedNotes[a.id] && (
                            <div style={{ marginTop: 14, borderTop: '1px solid var(--rasp-border)', paddingTop: 12 }}>
                              {(notes[a.id] || []).length === 0 ? (
                                <div style={{ fontSize: 12, color: 'var(--rasp-fg-3)', marginBottom: 10 }}>No notes yet.</div>
                              ) : (
                                (notes[a.id] || []).map(n => (
                                  <div key={n.id} style={{ marginBottom: 10 }}>
                                    <div style={{ fontSize: 13, color: 'var(--rasp-fg-2)', lineHeight: 1.5 }}>{n.note_text}</div>
                                    <div style={{ fontSize: 11, color: 'var(--rasp-fg-3)', marginTop: 2 }}>{n.created_at}</div>
                                  </div>
                                ))
                              )}
                              <textarea
                                rows={2}
                                placeholder="Add a note…"
                                value={newNote[a.id] || ''}
                                onChange={e => setNewNote(prev => ({ ...prev, [a.id]: e.target.value }))}
                                style={{ ...inputStyle, resize: 'vertical', marginBottom: 6 }}
                                onFocus={e => { e.currentTarget.style.borderColor = 'var(--rasp-teal-light)'; }}
                                onBlur={e => { e.currentTarget.style.borderColor = 'var(--rasp-border)'; }}
                              />
                              <button
                                onClick={() => handleAddNote(a.id)}
                                style={{ padding: '5px 14px', background: 'var(--rasp-teal-soft)', border: '1px solid var(--rasp-teal-edge)', borderRadius: 6, color: 'var(--rasp-teal-light)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                              >Add Note</button>
                            </div>
                          )}
                        </div>
                      ); })}
                    </div>
                  )); })()}
                </div>
              )}
            </div>
          );
        })}

        </>)}

        {mainTab === "archives" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--rasp-teal-light)" }}>Archived Projects</h2>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--rasp-fg-3)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={archivedAssignments.length > 0 && selectedArchived.size === archivedAssignments.length}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedArchived(new Set(archivedAssignments.map(p => p.id)));
                    else setSelectedArchived(new Set());
                  }}
                />
                Select All
              </label>
            </div>
            {archivedAssignments.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--rasp-fg-3)", padding: "24px 0" }}>No archived projects.</div>
            )}
            {archivedAssignments.map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "var(--rasp-surface)", border: "1px solid var(--rasp-border)", borderRadius: 8, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={selectedArchived.has(p.id)}
                  onChange={(e) => {
                    const next = new Set(selectedArchived);
                    if (e.target.checked) next.add(p.id); else next.delete(p.id);
                    setSelectedArchived(next);
                  }}
                />
                <span style={{ fontSize: 14, fontWeight: 600, color: "var(--rasp-fg-2)", flex: 1 }}>{p.name}</span>
                <span style={{ fontSize: 12, color: "var(--rasp-fg-3)" }}>{p.client}</span>
                <span style={{ fontSize: 11, color: "var(--rasp-fg-3)", fontFamily: "'DM Mono', monospace" }}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</span>
                <button
                  onClick={() => unarchiveProject(p.id)}
                  style={{ background: 'transparent', border: '1px solid var(--rasp-border)', borderRadius: 6, color: 'var(--rasp-fg-3)', fontSize: 11, fontWeight: 500, padding: '4px 12px', cursor: 'pointer', marginLeft: 8 }}
                >Unarchive</button>
              </div>
            ))}
          </div>
        )}

        </div>
        </div>
      </div>
    </div>
  );
}
